import { useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";

/**
 * The URL keys that belong to the app rather than to a screen: the Top bar's
 * period (`preset`/`from`/`to`), what it is compared against (`cmp`), which
 * date a conversion counts on (`by`) and whose data (`who`). A link from one
 * screen to another carries them, so «Кампании за 30 дней» → «Отчёты» stays
 * 30 days; everything screen-specific (filters, sort, page) stays behind.
 */
export const CARRIED_KEYS = ["preset", "from", "to", "cmp", "by", "who"] as const;

/** The carried part of a query string, as `?…`, or "" when there is none. Pure. */
export function carryPeriod(search: URLSearchParams | string): string {
  const from = typeof search === "string" ? new URLSearchParams(search) : search;
  const next = new URLSearchParams();
  for (const key of CARRIED_KEYS) {
    const value = from.get(key);
    if (value) next.set(key, value);
  }
  const s = next.toString();
  return s ? `?${s}` : "";
}

function trimSlash(path: string): string {
  return path.length > 1 && path.endsWith("/") ? path.slice(0, -1) : path;
}

/**
 * Which tab an address names: the first path segment after `base`. The bare
 * base, and anything that is not one of `tabs`, is `index` — a typo in a link
 * lands on the first tab instead of on nothing. Pure.
 *
 *   tabFromPath("/campaigns/42/flow", "/campaigns/42", TABS, "summary") === "flow"
 */
export function tabFromPath<T extends string>(pathname: string, base: string, tabs: readonly T[], index: T): T {
  const root = trimSlash(base);
  const path = trimSlash(pathname);
  if (path !== root && !path.startsWith(`${root}/`)) return index;
  const segment = path.slice(root.length + 1).split("/")[0] ?? "";
  return (tabs as readonly string[]).includes(segment) ? (segment as T) : index;
}

/** The address of a tab: `base` for the index tab, `base/<tab>` for the rest. Pure. */
export function tabPath<T extends string>(base: string, tab: T, index: T): string {
  const root = trimSlash(base);
  return tab === index ? root : `${root}/${tab}`;
}

export interface RouteTab<T extends string> {
  /** The tab the address names. */
  tab: T;
  /** A tab's address with the app's period carried — for a `<Link>` or an `<a>`. */
  href: (tab: T) => string;
  /** Goes to a tab, in history: Back returns to the previous tab. */
  setTab: (tab: T) => void;
}

/**
 * Tabs are addresses (decision 04): a tab is the path segment after the
 * screen's own address, so `/campaigns/:id/flow` opens the card on «Поток»,
 * a tab can be sent as a link, and Back walks the tabs. The route itself is
 * declared in App.tsx as `<base>/:tab`; this hook only reads and writes it.
 *
 *   const { tab, setTab } = useRouteTab(`/campaigns/${id}`, CARD_TABS, "summary");
 *   <TabBar value={tab} onValueChange={(v) => setTab(v as CardTab)} tabs={…} />
 */
export function useRouteTab<T extends string>(base: string, tabs: readonly T[], index: T): RouteTab<T> {
  const { pathname, search } = useLocation();
  const navigate = useNavigate();
  const tab = tabFromPath(pathname, base, tabs, index);
  const href = useCallback((next: T) => `${tabPath(base, next, index)}${carryPeriod(search)}`, [base, index, search]);
  const setTab = useCallback((next: T) => navigate(href(next)), [navigate, href]);
  return { tab, href, setTab };
}
